import { useCallback, useLayoutEffect, useState } from 'react'
import { Button, Col, Container, Form, Row, Spinner } from 'react-bootstrap'
import { IMaskInput } from 'react-imask'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import ConfirmationModal from '../../../../../components/ConfirmationModal'
import LoadingSpinner from '../../../../../components/LoadingSpinner/LoadingSpinner'
import SearchInput from '../../../../../components/SearchInput'
import { useLoading } from '../../../../../hooks/LoadingContext'
import DashboardLayout from '../../../../../layouts/Dashboard'
import api from '../../../../../services/api'
import { errorApi } from '../../../../../utils/errorApi'
import { formatDateTSX } from '../../../../../utils/tsxFormat'

import styles from './styles.module.css'
interface IVehicle {
  id: number
  name: string
}

interface IDriver {
  id: number
  name: string
}

const MultasEditar: React.FC = () => {
  const [type, setType] = useState<string>('')
  const [date, setDate] = useState<string>('')
  const [costString, setCostString] = useState<string>('')
  const [cost, setCost] = useState<number>(0)
  const [points, setPoints] = useState<number>(0)
  const [description, setDescription] = useState<string>('')
  const { isLoading, setIsLoading } = useLoading()

  const [vehicleId, setVehicleId] = useState<number>()
  const [driverId, setDriverId] = useState<number>()

  const [vehicles, setVehicles] = useState<IVehicle[]>()
  const [drivers, setDrivers] = useState<IDriver[]>()

  const [showVehicles, setShowVehicles] = useState<boolean>(true)
  const [showDrivers, setShowDrivers] = useState<boolean>(true)

  const [showModalRemove, setShowModalRemove] = useState<boolean>(false)

  const isValidModel = () => {
    if (!(type && date && cost && points && vehicleId && driverId)) return false
    return true
  }

  const loadDriverData = useCallback(async (searchDriver: string) => {
    setIsLoading(true)
    let apiUrl = '/delivery-manager/drivers?limit=10&show_invalid=1'

    if (searchDriver) apiUrl += '&name=' + searchDriver

    try {
      const { data } = await api.get(apiUrl)

      if (!data.drivers.length) {
        toast.warning('Não foram encontrados resultados para a pesquisa!')
        setDrivers(undefined)
      } else {
        setDrivers(
          data.drivers.map((driver: { user_id: any; name: any }) => {
            return { id: driver.user_id, name: driver.name }
          }),
        )
      }
      setIsLoading(false)
    } catch (err) {
      toast.error('Falha ao buscar os dados! Atualize a página.')
      setIsLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const loadVehicleData = useCallback(async (searchBoard: string) => {
    setIsLoading(true)
    let apiUrl = '/delivery-manager/vehicles?limit=10&show_invalid=1'

    if (searchBoard) apiUrl += '&board=' + searchBoard

    try {
      const { data } = await api.get(apiUrl)

      if (!data.length) {
        toast.warning('Não foram encontrados resultados para a pesquisa!')
        setVehicles(undefined)
      } else {
        setVehicles(
          data.map((vehicle: { id: any; board: any; model: any }) => {
            return {
              id: vehicle.id,
              name: `${vehicle.board} - ${vehicle.model}`,
            }
          }),
        )
      }
      setIsLoading(false)
    } catch (err) {
      toast.error('Falha ao buscar os dados! Atualize a página.')
      setIsLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleCloseModalRemove = () => {
    setShowModalRemove(false)
  }

  const handleShowModalRemove = () => {
    setShowModalRemove(true)
  }

  let { id } = useParams()

  useLayoutEffect(() => {
    async function loadData() {
      setIsLoading(true)
      try {
        let { data } = await api.get(`/delivery-manager/traffic-tickets/${id}`)
        let driver_id = data.driver_id
        let vehicle_id = data.vehicle_id

        setType(data.type)
        setDescription(data.description)
        setDate(formatDateTSX(data.date, "yyyy-MM-dd'T'kk:mm"))
        setCost(data.cost)
        setCostString(data.cost.toString().replace('.', ','))
        setPoints(data.points)
        setVehicleId(data.vehicle_id)
        setDriverId(data.driver_id)

        data = await api.get(`/delivery-manager/vehicles/${vehicle_id}`)
        setVehicles([{ id: data.data.id, name: `${data.data.board} - ${data.data.model}` }])

        data = await api.get(`/delivery-manager/drivers/${driver_id}`)
        setDrivers([{ id: data.data.user_id, name: data.data.name }])
        setIsLoading(false)
      } catch (err) {
        toast.error('Falha ao buscar os dados! Atualize a página.')
        setIsLoading(false)
      }
    }
    loadData()
  }, [id, setIsLoading])

  const navigate = useNavigate()

  const handleVehicleClearInput = () => {
    setShowVehicles(false)
  }

  const handleVehicleSearch = (searchBoard: string) => {
    loadVehicleData(searchBoard)
    setShowVehicles(true)
  }

  const handleDriverClearInput = () => {
    setShowDrivers(false)
  }

  const handleDriverSearch = (searchDriver: string) => {
    loadDriverData(searchDriver)
    setShowDrivers(true)
  }

  const handleSubmit = async (event: any) => {
    event.preventDefault()

    setIsLoading(true)

    try {
      await api.put(`/delivery-manager/traffic-tickets/${id}`, {
        driver_id: driverId,
        vehicle_id: vehicleId,
        type: type,
        description: description,
        date: new Date(date).toISOString(),
        cost: cost,
        points: points,
      })

      setIsLoading(false)
      toast.info('Multa atualizada com sucesso!', { autoClose: 3000 })
      navigate('/gestor-entregas/veiculos/multas')
    } catch (err) {
      toast.error(errorApi(err, 'Falha ao atualizar a multa! Verifique os dados.'))
      setIsLoading(false)
    }
  }

  const handleRemove = async (event: any) => {
    event.preventDefault()
    setIsLoading(true)

    try {
      await api.delete(`/delivery-manager/traffic-tickets/${id}`)
      toast.info('Multa removida com sucesso!', { autoClose: 3000 })
      setIsLoading(false)
      navigate('/gestor-entregas/veiculos/multas')
    } catch (err) {
      toast.error(errorApi(err, 'Falha ao remover a multa! Tente novamente.'))
      setIsLoading(false)
    }
  }

  return (
    <DashboardLayout backgroundColor="#F8F8F8" pageName="Editar multa">
      <LoadingSpinner text="Salvando informações. Aguarde..." />
      <Container>
        <h2 style={{ textAlign: 'center' }}>Editar multa</h2>

        <Col className={styles.formContainer}>
          <Row>
            <Col md={6}>
              <Row style={{ marginTop: 15 }}>
                <Form.Label className={styles.label}>Nome do motorista*</Form.Label>
                <SearchInput
                  data={drivers}
                  showOptions={showDrivers}
                  setValue={setDriverId}
                  handleClear={handleDriverClearInput}
                  handleSearch={handleDriverSearch}
                  placeholder="Digite o nome do motorista"
                  edit={true}
                />
              </Row>
            </Col>
            <Col md={6}>
              <Row style={{ marginTop: 15 }}>
                <Form.Label className={styles.label}>Placa do veículo*</Form.Label>
                <SearchInput
                  data={vehicles}
                  showOptions={showVehicles}
                  setValue={setVehicleId}
                  handleClear={handleVehicleClearInput}
                  handleSearch={handleVehicleSearch}
                  placeholder="Digite a placa do veículo"
                  edit={true}
                />
              </Row>
            </Col>
          </Row>
          <Row style={{ marginTop: 0, textAlign: 'left' }}>
            <Col md={6} style={{ marginTop: 15 }}>
              <Form.Label className={styles.label}>Tipo da multa*</Form.Label>
              <Form.Control
                className={styles.input}
                placeholder="Digite aqui o tipo da multa"
                value={type}
                onChange={(event) => setType(event.target.value)}
              ></Form.Control>
            </Col>
            <Col md={6} style={{ marginTop: 15 }}>
              <Form.Label className={styles.label}>Data*</Form.Label>
              <Form.Control
                className={styles.input}
                type="datetime-local"
                value={date}
                onChange={(event) => setDate(event.target.value)}
              />
            </Col>
          </Row>
          <Row style={{ marginTop: 0, textAlign: 'left' }}>
            <Col md={6} style={{ marginTop: 15 }}>
              <Form.Label className={styles.label}>Custo*</Form.Label>
              <Form.Control
                className={styles.input}
                as={IMaskInput}
                mask={[
                  {
                    mask: Number,
                    radix: ',',
                  },
                ]}
                placeholder="Digite aqui o custo da multa"
                value={costString}
                onChange={(event) => {
                  setCostString(event.target.value)
                  setCost(Number(event.target.value.replace(',', '.')))
                }}
              ></Form.Control>
            </Col>
            <Col md={6} style={{ marginTop: 15 }}>
              <Form.Label className={styles.label}>Número de pontos*</Form.Label>
              <Form.Control
                className={styles.input}
                placeholder="Digite aqui os pontos na CNH da multa"
                value={points}
                onChange={(event) => setPoints(Number(event.target.value) || 0)}
              ></Form.Control>
            </Col>
          </Row>
          <Row style={{ marginTop: 0, textAlign: 'left' }}>
            <Col style={{ marginTop: 15 }}>
              <Form.Label className={styles.label}>Descrição</Form.Label>
              <Form.Control
                className={styles.input}
                style={{
                  resize: 'none',
                }}
                as="textarea"
                rows={3}
                placeholder="Descrição da multa"
                value={description}
                onChange={(event) => setDescription(event.target.value)}
              />
            </Col>
          </Row>
          <Col style={{ textAlign: 'right' }}>
            <Button
              className={styles.deleteBtn}
              disabled={isLoading}
              onClick={handleShowModalRemove}
            >
              {isLoading ? (
                <Spinner
                  animation="border"
                  size="sm"
                  style={{
                    color: '#fff',
                    marginRight: 10,
                  }}
                />
              ) : (
                'Remover multa'
              )}
            </Button>
            <Button
              className={styles.btn_submit}
              disabled={isLoading || !isValidModel()}
              onClick={handleSubmit}
            >
              {isLoading ? (
                <Spinner
                  animation="border"
                  size="sm"
                  style={{
                    color: '#fff',
                    marginRight: 10,
                  }}
                />
              ) : (
                'Atualizar multa'
              )}
            </Button>
          </Col>
        </Col>
      </Container>
      <ConfirmationModal
        show={showModalRemove}
        message="Tem certeza de que deseja remover a multa?"
        action={handleRemove}
        closeModal={handleCloseModalRemove}
      ></ConfirmationModal>
    </DashboardLayout>
  )
}

export default MultasEditar
